import { useEffect, useRef, useState } from 'react';
import { READONLY } from '../lib/readonly';

// Inline-editable heading. Click the name to turn it into an input, save
// on blur or Enter, cancel on Escape. Used in ProjectView and ClientDashboard
// headers to rename builds and clients without touching their slug.
//
// Slugs (technical identifiers) stay frozen. Only the display name changes.
// The FKs into tb_projects.slug / tb_clients.slug are NO ACTION on update,
// so a slug rename would be rejected by Postgres — we don't go there.
export function EditableName({
  value,
  onSave,
  className = '',
  inputClassName = '',
  placeholder = 'name…',
  maxLength = 200,
}: {
  value: string;
  onSave: (next: string) => Promise<void> | void;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  maxLength?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 10);
    }
  }, [editing]);

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === value) {
      setEditing(false);
      setDraft(value);
      setError(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
    setError(null);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => !READONLY && setEditing(true)}
        disabled={READONLY}
        className={
          'text-left hover:text-amberHi transition-colors ' +
          (READONLY ? 'cursor-default' : 'cursor-text') +
          ' ' +
          className
        }
        title={READONLY ? 'readonly mode' : 'click to rename'}
      >
        {value}
      </button>
    );
  }

  return (
    <div className="flex-1 space-y-1">
      <input
        ref={inputRef}
        type="text"
        value={draft}
        maxLength={maxLength}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') cancel();
        }}
        placeholder={placeholder}
        className={
          'w-full bg-panelHi border border-amber/60 rounded px-2 py-1 outline-none ' +
          inputClassName
        }
      />
      {error && (
        <div className="text-xs font-mono text-red">{error}</div>
      )}
    </div>
  );
}
